import React, { useRef } from 'react';
import css from './MoreListings.module.css';
import classNames from 'classnames';
import { createSlug } from '../../../util/urlHelpers';
import { NamedLink, FavoriteButton, ResponsiveImage } from '../../../components';
import { priceData } from '../ListingPage.shared';
import { types as sdkTypes } from '../../../util/sdkLoader';

const { Money } = sdkTypes;

const ensureMoney = price => {
  if (!price) {
    return null;
  }

  if (price instanceof Money) {
    return price;
  }

  const amount = price.amount;
  const currency = price.currency;
  const numericAmount = typeof amount === 'number' ? amount : Number(amount);

  return Number.isFinite(numericAmount) && currency ? new Money(numericAmount, currency) : null;
};

const formatDimensionValue = (value, suffix) => {
  if (value == null) return null;

  const stringValue = String(value).trim();
  let inchValue = stringValue.includes('_inch')
    ? stringValue.split('_inch')[0]
    : stringValue.replace(/inch(?:es)?/i, '');
  const isCompactDecimalInch =
    stringValue.includes('_inch') &&
    !stringValue.includes('_frame_width') &&
    ['W', 'D'].includes(suffix) &&
    /^\d{2,3}$/.test(inchValue);
  if (isCompactDecimalInch) {
    inchValue = `${inchValue.slice(0, 1)}_${inchValue.slice(1)}`;
  }
  inchValue = inchValue.replace(/"/g, '').trim();
  const numericCandidate = inchValue.replace(/_/g, '.');
  const numericValue = Number(numericCandidate);
  const isNumericValue = !Number.isNaN(numericValue);
  const displayValue = isNumericValue ? String(numericValue) : inchValue.replace(/_/g, ' ');

  return displayValue ? `${displayValue}${isNumericValue ? '"' : ''}${suffix}` : null;
};

const findFieldValue = (publicData, keys, labelPattern) => {
  const fields = [
    publicData?.requiredFieldsArray,
    publicData?.optionalFieldsArray,
  ].filter(Array.isArray).flat();
  const normalizedKeys = keys.map(key => key.toLowerCase());
  const byKey = fields.find(field => normalizedKeys.includes((field?.key || '').toLowerCase()));
  const directValue = keys
    .map(key => publicData?.[key] ?? publicData?.[`pub_${key}`])
    .find(value => value != null && value !== '');
  const byLabel = labelPattern ? fields.find(field => labelPattern.test(field?.label || '')) : null;

  return byKey?.value ?? directValue ?? byLabel?.value ?? null;
};

const shouldHideDimensions = publicData => {
  const categoryValues = [
    publicData?.categoryLevel,
    publicData?.categoryLevel1,
    publicData?.categoryLevel2,
    publicData?.categoryLevel3,
    publicData?.subCategoryLevel,
  ];
  return categoryValues.some(value =>
    /row[\s_-]*spacers?/i.test(String(value || ''))
  );
};

const MoreListings = props => {
  const { listings, config, intl } = props;
  const trackRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(false);


  if (!listings || listings.length === 0) {
    return null;
  }

  // Check if content is scrollable
  const checkScrollable = () => {
    const node = trackRef.current;
    if (!node) return;

    const isScrollable = node.scrollWidth > node.clientWidth;
    const atStart = node.scrollLeft <= 0;
    const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;

    setShowLeftArrow(isScrollable && !atStart);
    setShowRightArrow(isScrollable && !atEnd);
  };

  React.useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, [listings]);

  React.useEffect(() => {
    const node = trackRef.current;
    if (node) {
      node.addEventListener('scroll', checkScrollable);
      return () => node.removeEventListener('scroll', checkScrollable);
    }
  }, []);

  const scrollByCards = direction => {
    const node = trackRef.current;
    if (!node) return;
    const card = node.querySelector(`.${css.card}`);
    const cardWidth = card ? card.getBoundingClientRect().width : 300;
    const gap = 24;
    node.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
  };

  // Extract brand and dimensions from listing data
  const getListingInfo = listing => {
    const { publicData } = listing.attributes;
    const brandName =
      listing.author?.attributes?.profile?.displayName || 'Unknown Brand';

    // Extract dimensions from publicData
    const getDimensions = () => {
      const { beam_length, beam_width } = publicData || {};
      if (beam_length && beam_width) {
        const length = beam_length.replace('_inch', '"').replace('_inch', '"');
        const width = beam_width.replace('_inch', '"').replace('_inch', '"');
        return `${length}W x ${width}H`;
      }
      return null;
    };

    return {
      brand: brandName,
      dimensions: getDimensions(),
    };
  };

  return (
    <section className={css.featuredListings}>
      <div className={css.header}>
        <h2 className={css.title}>More Listings</h2>

      </div>

      <div className={css.slider}>
        {showLeftArrow && (
          <button
            aria-label="Previous"
            className={css.navBtn}
            onClick={() => scrollByCards(-1)}
          >
            ‹
          </button>
        )}
        <div className={css.track} ref={trackRef}>
          {listings.map(l => {
            const { title, price, publicData } = l.attributes;
            const { optionalFieldsArray } = publicData || {};
            const rawManufacturerShow = optionalFieldsArray?.find(field => field.key === 'manufacturer')?.value;
            const manufacturerShow =
              rawManufacturerShow?.toLowerCase().includes('other')
                ? (publicData?.pub_manufacturer_other || rawManufacturerShow)
                : rawManufacturerShow;
            const length = findFieldValue(publicData, ['length', 'beam_length'], /length/i);
            const width = findFieldValue(publicData, ['width'], /width/i);
            const height = findFieldValue(publicData, ['height'], /height/i);
            const dimensions = (() => {
              if (shouldHideDimensions(publicData)) return null;
              const parts = [];
              if (length) {
                parts.push(formatDimensionValue(length, 'L'));
              }
              if (width) {
                parts.push(formatDimensionValue(width, 'W'));
              }
              if (height) {
                parts.push(formatDimensionValue(height, 'H'));
              }
              const validParts = parts.filter(Boolean);
              return validParts.length > 0 ? validParts.join(' x ') : null;
            })();
            const { formattedPrice } = priceData(ensureMoney(price), config.currency, intl);

            const image = l.images && l.images.length > 0 ? l.images[0] : null;
            const isImageString = typeof image === 'string';
            const responsiveImageProps = isImageString
              ? { url: image, variants: [] }
              : { image: image, variants: ['listing-card'] };

            const { availability, preSaleDate } = publicData || {};
            const isPreSale = availability === 'pre-sale';
            let availabilityLabel = null;
            if (isPreSale && preSaleDate) {
              const date = new Date(Number(preSaleDate) * 1000);
              const month = date.toLocaleString('default', { month: 'long' });
              const year = date.getFullYear();
              availabilityLabel = `AVAILABLE ${month} ${year}`.toUpperCase();
            }

            return (
              <NamedLink
                name="ListingPage"
                params={{ id: l.id.uuid, slug: createSlug(title) }}
                key={l.id.uuid}
                className={css.cardLink}
              >
                <article className={isPreSale ? css.precard : css.card}>
                  <div className={css.imageWrap}>
                    {isPreSale ? (
                      <span className={css.tag}>Pre-Sale</span>
                    ) : null}
                    {image ? (
                      <ResponsiveImage
                        alt={title || 'Featured listing'}
                        className={css.image}
                        {...responsiveImageProps}
                      />
                    ) : (
                      <div className={css.fallbackImage}>
                        <img
                          src="/static/images/pallet-default.png"
                          alt="No image available"
                        />
                      </div>
                    )}
                    <div className={css.favoriteButtonWrapper}>
                      <FavoriteButton listingId={l.id} />
                    </div>
                  </div>
                  <div className={css.cardBody}>
                    <p className={css.price}>{formattedPrice || 'Contact for price'}</p>
                    <h3 className={css.cardTitle}>{title}</h3>
                    <div className={css.brandWrapper}>
                      {manufacturerShow && <p className={css.brand}>{manufacturerShow}</p>}
                      {manufacturerShow && dimensions && `|`}
                      {dimensions && (
                        <>
                          <p className={css.dimensions}> {dimensions}</p>
                        </>
                      )}
                    </div>
                    {availabilityLabel ? (
                      <p className={css.availability}>{availabilityLabel}</p>
                    ) : <div className={css.availabilityEmpty}></div>}
                  </div>
                </article>
              </NamedLink>
            );
          })}
        </div>
        {showRightArrow && (
          <button
            aria-label="Next"
            className={css.navBtn}
            onClick={() => scrollByCards(1)}
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
};

export default MoreListings;
